import React from 'react';
import styled from 'styled-components';
import {rem} from 'polished';

import TextBlockArray from '~/src/components/textBlockArray/TextBlockArray';
import Image from '~/src/components/image/Image';
import Link from '~/src/components/link/Link';

import {FeedContentImage, FeedContentTextBlockArray} from '~/src/types/feed/content';
import {getContentUrl} from '~/src/utilities/helpers/link';

import variables from '~/src/css/global.module.scss';

interface InternalTeaserLink {
    titleHeader?: string;
    title: string;
    url: string;
}

interface DynamicTeaserBoxProps {
    title: FeedContentTextBlockArray;
    teaserImage?: FeedContentImage;
    teasers: InternalTeaserLink[];
}

const Container = styled.section`
    background-color: ${variables.mainBackgroundColor};
    color: ${variables.contentTextColor};
    padding-bottom: ${rem(10)};

    @media only screen and (min-width: ${variables.breakpointDesktop}) {
        padding-bottom: ${rem(20)};
    }
`;

const Title = styled.div`
    font-size: ${rem(28)};
    font-weight: ${variables.fontWeightBlack};
    letter-spacing: ${rem(-0.4)};
    line-height: ${rem(32)};
    padding: ${rem(10)};

    @media only screen and (min-width: ${variables.breakpointTablet}) {
        font-size: ${rem(32)};
        line-height: ${rem(38)};
    }

    @media only screen and (min-width: ${variables.breakpointDesktop}) {
        font-size: ${rem(40)};
        line-height: ${rem(44)};
        padding: ${rem(20)};
    }
`;

const Teasers = styled.div`
    font-weight: ${variables.fontWeightBold};
`;

const Teaser = styled(Link)`
    align-items: center;
    color: currentColor;
    display: block;
    margin: 0 ${rem(10)};

    @media only screen and (min-width: ${variables.breakpointDesktop}) {
        margin: 0 ${rem(20)};
    }

    & + & {
        margin-top: ${rem(10)};

        @media only screen and (min-width: ${variables.breakpointDesktop}) {
            margin-top: ${rem(20)};
        }
    }
`;

const TeaserTitleHeader = styled.span`
    display: block;
    font-size: ${rem(12)};
    letter-spacing: ${rem(0.2)};
    line-height: ${rem(15)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media only screen and (min-width: ${variables.breakpointDesktop}) {
        font-size: ${rem(14)};
        line-height: ${rem(18)};
    }
`;

const TeaserTitle = styled.div`
    display: block;
    font-size: ${rem(16)};
    letter-spacing: ${rem(-0.4)};
    line-height: ${rem(16)};

    @media only screen and (min-width: ${variables.breakpointDesktop}) {
        font-size: ${rem(18)};
        line-height: ${rem(22)};
    }
`;

const DynamicTeaserBox: React.FC<DynamicTeaserBoxProps> = ({title, teaserImage, teasers}) => {
    const filteredTeasers = teasers.filter(({url}) => Boolean(url));

    if (filteredTeasers.length < 1) {
        return null;
    }

    return (
        <Container>
            {teaserImage?.image && <Image {...teaserImage.image} />}
            <Title>
                <TextBlockArray {...title} />
            </Title>
            <Teasers>
                {filteredTeasers.map(({titleHeader, title: teaserTitle, url}, index) => (
                    <Teaser href={getContentUrl(url)} key={index}>
                        <TeaserTitle>
                            {titleHeader && <TeaserTitleHeader>{titleHeader}</TeaserTitleHeader>}
                            {teaserTitle}
                        </TeaserTitle>
                    </Teaser>
                ))}
            </Teasers>
        </Container>
    );
};

export default DynamicTeaserBox;
